// Auth seam, backed by Supabase. Exposes a small AuthUser shape (Supabase id,
// email, JWT) decoupled from the app's UserProfile in the snapshot — the app
// reads `current()` for "is the user signed in", and the synced repository
// reads it for the bearer token.
//
// Supports four sign-in paths: email magic-link, email + password,
// Google OAuth, and the password-reset flow. The reset flow is special: when
// a user clicks the link in their reset email, Supabase fires PASSWORD_RECOVERY
// on onAuthStateChange. We surface that as a separate recovery channel so the
// app can render a "set new password" screen instead of treating the recovery
// session as a normal sign-in.

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

export interface AuthUser {
  id: string;
  email?: string;
  displayName?: string;
  /** Bearer token to attach to /api/sync requests. */
  jwt: string;
}

/** Result of signUpWithPassword. When email confirmation is required (Supabase
 *  default), the user must click the link before they can sign in — the UI
 *  shows a "check your inbox" state. Otherwise they're signed in immediately. */
export type SignUpResult = { kind: 'confirmation_sent' } | { kind: 'signed_in' };

export interface Auth {
  current(): AuthUser | null;
  /** Subscribe to changes. The handler is invoked synchronously with the current value. */
  subscribe(handler: (user: AuthUser | null) => void): () => void;
  /** Subscribe to PASSWORD_RECOVERY mode (user landed via a reset-link). Fires
   *  synchronously with the current value. */
  subscribeRecovery(handler: (inRecovery: boolean) => void): () => void;
  /** True if the user just arrived via a password-reset link. */
  isRecoveryMode(): boolean;
  signInWithEmail(email: string): Promise<void>;
  signInWithPassword(email: string, password: string): Promise<void>;
  signUpWithPassword(email: string, password: string): Promise<SignUpResult>;
  requestPasswordReset(email: string): Promise<void>;
  updatePassword(newPassword: string): Promise<void>;
  signInWithGoogle(): Promise<void>;
  signOut(): Promise<void>;
  /** True once the initial getSession() has resolved — used by the App boot gate. */
  ready(): boolean;
}

function userFromSession(session: Session | null): AuthUser | null {
  if (!session?.user) return null;
  const u = session.user;
  return {
    id: u.id,
    ...(u.email ? { email: u.email } : {}),
    ...(u.user_metadata?.full_name ? { displayName: u.user_metadata.full_name as string } : {}),
    jwt: session.access_token,
  };
}

class SupabaseAuth implements Auth {
  private client: SupabaseClient;
  private user: AuthUser | null = null;
  private listeners = new Set<(u: AuthUser | null) => void>();
  private recoveryListeners = new Set<(r: boolean) => void>();
  private hasResolvedSession = false;
  private inRecovery = false;

  constructor(url: string, anonKey: string) {
    this.client = createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true, // parses tokens from magic-link / reset-link redirects
      },
    });

    // Hydrate on boot. If the user clicked a magic link or reset link, the
    // session is already in the URL hash and detectSessionInUrl will populate
    // it before this resolves.
    void this.client.auth.getSession().then(({ data }) => {
      this.user = userFromSession(data.session);
      this.hasResolvedSession = true;
      this.notify();
    });

    this.client.auth.onAuthStateChange((event, session) => {
      this.user = userFromSession(session);
      this.hasResolvedSession = true;
      // PASSWORD_RECOVERY fires when the user lands via a reset-password link.
      // Stay in recovery mode until updatePassword() succeeds (which clears
      // the flag) so a refresh during recovery still routes to the reset
      // screen rather than dropping the user into Discover.
      if (event === 'PASSWORD_RECOVERY') {
        this.inRecovery = true;
        this.notifyRecovery();
      }
      this.notify();
    });
  }

  current(): AuthUser | null {
    return this.user;
  }

  ready(): boolean {
    return this.hasResolvedSession;
  }

  isRecoveryMode(): boolean {
    return this.inRecovery;
  }

  subscribe(handler: (u: AuthUser | null) => void): () => void {
    this.listeners.add(handler);
    handler(this.user);
    return () => {
      this.listeners.delete(handler);
    };
  }

  subscribeRecovery(handler: (r: boolean) => void): () => void {
    this.recoveryListeners.add(handler);
    handler(this.inRecovery);
    return () => {
      this.recoveryListeners.delete(handler);
    };
  }

  async signInWithEmail(email: string): Promise<void> {
    const { error } = await this.client.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw error;
  }

  async signInWithPassword(email: string, password: string): Promise<void> {
    const { error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }

  async signUpWithPassword(email: string, password: string): Promise<SignUpResult> {
    const { data, error } = await this.client.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw error;
    // When email confirmation is required (Supabase default), signUp returns
    // a user without a session. Otherwise the session is populated and the
    // onAuthStateChange listener will sign the user in.
    return data.session ? { kind: 'signed_in' } : { kind: 'confirmation_sent' };
  }

  async requestPasswordReset(email: string): Promise<void> {
    const { error } = await this.client.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) throw error;
  }

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await this.client.auth.updateUser({ password: newPassword });
    if (error) throw error;
    // Recovery is complete — the session is now a normal one. Clear the flag
    // so App.tsx exits the reset screen on the next render.
    this.inRecovery = false;
    this.notifyRecovery();
  }

  async signInWithGoogle(): Promise<void> {
    const { error } = await this.client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
  }

  async signOut(): Promise<void> {
    const { error } = await this.client.auth.signOut();
    if (error) throw error;
    this.inRecovery = false;
    this.notifyRecovery();
  }

  private notify(): void {
    this.listeners.forEach((l) => l(this.user));
  }

  private notifyRecovery(): void {
    this.recoveryListeners.forEach((l) => l(this.inRecovery));
  }
}

class StubAuth implements Auth {
  private listeners = new Set<(u: AuthUser | null) => void>();
  private recoveryListeners = new Set<(r: boolean) => void>();
  current(): AuthUser | null {
    return null;
  }
  ready(): boolean {
    return true;
  }
  isRecoveryMode(): boolean {
    return false;
  }
  subscribe(handler: (u: AuthUser | null) => void): () => void {
    this.listeners.add(handler);
    handler(null);
    return () => {
      this.listeners.delete(handler);
    };
  }
  subscribeRecovery(handler: (r: boolean) => void): () => void {
    this.recoveryListeners.add(handler);
    handler(false);
    return () => {
      this.recoveryListeners.delete(handler);
    };
  }
  async signInWithEmail(): Promise<void> {
    throw new Error('Auth not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.');
  }
  async signInWithPassword(): Promise<void> {
    throw new Error('Auth not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.');
  }
  async signUpWithPassword(): Promise<SignUpResult> {
    throw new Error('Auth not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.');
  }
  async requestPasswordReset(): Promise<void> {
    throw new Error('Auth not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.');
  }
  async updatePassword(): Promise<void> {
    throw new Error('Auth not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.');
  }
  async signInWithGoogle(): Promise<void> {
    throw new Error('Auth not configured.');
  }
  async signOut(): Promise<void> {
    /* no-op */
  }
}

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

let auth: Auth = url && key ? new SupabaseAuth(url, key) : new StubAuth();

export function getAuth(): Auth {
  return auth;
}

export function setAuth(impl: Auth): void {
  auth = impl;
}
